import { FieldType } from '../../../../../models/form/fieldType';
import { Actions, ComparisonOperators } from '../../../../../models/form/rule';
import moment from 'moment';
import { noop } from '../../../../../utilities/FunctionUtilities';

export enum FieldValueType {
  BOOLEAN = 'BOOLEAN',
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  STRINGARRAY = 'STRINGARRAY',
}

export const RulesTemplates = {
  [FieldType.Text]: {
    operators: [ComparisonOperators.IsEqual, ComparisonOperators.IsCompleted], // Possible operators on this field to create a rule
    inputField: { type: FieldType.Text }, // to render input field in rules modal
    formatValueType: (value: string): string => value, // to format when comparing with the condition value
    actions: [Actions.IsRequired, Actions.IsVisible], // Possible actions for this field when it's the target of a rule
  },
  [FieldType.LongText]: {
    operators: [ComparisonOperators.IsEqual, ComparisonOperators.IsCompleted],
    inputField: { type: FieldType.Text },
    formatValueType: (value: string): string => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Date]: {
    operators: [
      ComparisonOperators.IsEqual,
      ComparisonOperators.GreaterThan,
      ComparisonOperators.LowerThan,
      ComparisonOperators.EqualOrGreaterThan,
      ComparisonOperators.EqualOrLowerThan,
      ComparisonOperators.IsCompleted,
    ],
    inputField: { type: FieldType.Datetime, instance: 'date' },
    formatValueType: (value: string): moment.Moment => moment(value).utc(true),
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Datetime]: {
    operators: [
      ComparisonOperators.IsEqual,
      ComparisonOperators.GreaterThan,
      ComparisonOperators.LowerThan,
      ComparisonOperators.EqualOrGreaterThan,
      ComparisonOperators.EqualOrLowerThan,
      ComparisonOperators.IsCompleted,
    ],
    inputField: { type: FieldType.Datetime, instance: 'datetime' },
    formatValueType: (value: string): moment.Moment => moment(value).utc(true),
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Time]: {
    operators: [
      ComparisonOperators.IsEqual,
      ComparisonOperators.GreaterThan,
      ComparisonOperators.LowerThan,
      ComparisonOperators.EqualOrGreaterThan,
      ComparisonOperators.EqualOrLowerThan,
      ComparisonOperators.IsCompleted,
    ],
    inputField: { type: FieldType.Datetime, instance: 'time' },
    formatValueType: (value: string): moment.Moment => moment(value, 'HH:mm'),
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Birthdate]: {
    operators: [
      ComparisonOperators.IsEqual,
      ComparisonOperators.GreaterThan,
      ComparisonOperators.LowerThan,
      ComparisonOperators.EqualOrGreaterThan,
      ComparisonOperators.EqualOrLowerThan,
      ComparisonOperators.IsCompleted,
    ],
    inputField: { type: FieldType.Datetime, instance: 'date' },
    formatValueType: (value: string): moment.Moment => moment(value).utc(true),
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Numeric]: {
    operators: [
      ComparisonOperators.IsEqual,
      ComparisonOperators.GreaterThan,
      ComparisonOperators.LowerThan,
      ComparisonOperators.EqualOrGreaterThan,
      ComparisonOperators.EqualOrLowerThan,
      ComparisonOperators.IsCompleted,
    ],
    inputField: { type: FieldType.Numeric },
    formatValueType: (value: string): number => parseFloat(value as string),
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.CheckBox]: {
    operators: [ComparisonOperators.IsChecked],
    inputField: {},
    formatValueType: (value: boolean): boolean => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Toggle]: {
    operators: [ComparisonOperators.IsChecked],
    inputField: {},
    formatValueType: (value: boolean): boolean => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.EmailAddress]: {
    operators: [ComparisonOperators.IsEqual, ComparisonOperators.IsCompleted],
    inputField: { type: FieldType.Text },
    formatValueType: (value: string): string => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Select]: {
    operators: [ComparisonOperators.IsEqual, ComparisonOperators.IsCompleted],
    inputField: { type: FieldType.Select },
    formatValueType: (value: string): string => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.OptionList]: {
    operators: [ComparisonOperators.IsEqual, ComparisonOperators.IsChecked, ComparisonOperators.Includes],
    inputField: { type: FieldType.Select },
    formatValueType: (value: string[]): string[] => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Rating]: {
    operators: [
      ComparisonOperators.IsEqual,
      ComparisonOperators.GreaterThan,
      ComparisonOperators.LowerThan,
      ComparisonOperators.EqualOrGreaterThan,
      ComparisonOperators.EqualOrLowerThan,
      ComparisonOperators.IsCompleted,
    ],
    inputField: { type: FieldType.Rating },
    formatValueType: (value: number): number => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Acknowledgment]: {
    operators: [ComparisonOperators.IsChecked],
    inputField: {},
    formatValueType: (value: boolean): boolean => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.YesNo]: {
    operators: [ComparisonOperators.IsEqual, ComparisonOperators.IsCompleted],
    inputField: { type: FieldType.Select },
    formatValueType: (value: string): string => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.Signature]: {
    operators: [ComparisonOperators.IsCompleted],
    inputField: {},
    formatValueType: (value: string): string => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.PaymentOptionList]: {
    operators: [ComparisonOperators.IsEqual, ComparisonOperators.IsChecked],
    inputField: { type: FieldType.Select },
    formatValueType: (value: string[]): string[] => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.PaymentChoiceList]: {
    operators: [ComparisonOperators.IsEqual, ComparisonOperators.IsChecked, ComparisonOperators.Includes],
    inputField: { type: FieldType.Select },
    formatValueType: (value: string[]): string[] => value,
    actions: [Actions.IsRequired, Actions.IsVisible],
  },
  [FieldType.StaticText]: {
    operators: [],
    inputField: {},
    formatValueType: (): (() => void) => noop,
    actions: [Actions.IsVisible],
  },
  [FieldType.WebLink]: {
    operators: [],
    inputField: {},
    formatValueType: (): (() => void) => noop,
    actions: [Actions.IsVisible],
  },
  [FieldType.HTML]: {
    operators: [],
    inputField: {},
    formatValueType: (): (() => void) => noop,
    actions: [Actions.IsVisible],
  },
  [FieldType.DocumentViewer]: {
    operators: [],
    inputField: {},
    formatValueType: (): (() => void) => noop,
    actions: [Actions.IsVisible],
  },
  [FieldType.EmbeddedPhoto]: {
    operators: [],
    inputField: {},
    formatValueType: (): (() => void) => noop,
    actions: [Actions.IsVisible],
  },
  [FieldType.Video]: {
    operators: [],
    inputField: {},
    formatValueType: (): (() => void) => noop,
    actions: [Actions.IsVisible],
  },
  [FieldType.Products]: {
    operators: [],
    inputField: {},
    formatValueType: (): (() => void) => noop,
    actions: [Actions.IsVisible],
  },
};
